import { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';

import { fetchPeriods } from '../../../services/syntiro_honey/data_ingestion.service';
import { PeriodLookupItem } from '../../../types/syntiro_honey';

interface PeriodSelectProps {
  /** Field label. */
  label: string;
  /** Selected reporting-period id, or null when nothing is selected. */
  value: string | null;
  /**
   * Called with the chosen period id, or null when the empty option is picked. The
   * period's display label comes along so a caller can name the selection without
   * re-fetching the list (the active-filter row would otherwise show a raw uuid);
   * handlers that don't need it simply take one argument.
   */
  onChange: (periodId: string | null, periodLabel?: string) => void;
  /**
   * When true, render a leading empty option (e.g. "All periods" for the filter bar).
   * Omitted in the edit form, where a period is always one of the listed values.
   */
  allowEmpty?: boolean;
  /** Label for the empty option (defaults to "All periods"). */
  emptyLabel?: string;
  /**
   * Scope to this company's own reporting periods (OQ25: FY calendars differ per
   * client — the edit form passes the record's company so a correction can only
   * pick from that client's calendar — empty when none is seeded, never another
   * client's periods). Omit/null for every period (filter bar).
   */
  companyId?: string | null;
  /**
   * Display label of the currently saved period, for the fallback option when that
   * period is not in the (company-scoped) list — e.g. a record whose period predates
   * the company's seeded calendar. Without it the control would render BLANK for a
   * record that HAS a period, reading as "no period" to the reviewer.
   */
  currentLabel?: string | null;
  /** Disable the control. */
  disabled?: boolean;
}

/**
 * Reporting-period dropdown backed by `GET /periods`, shared by the review filter
 * bar (filter by period, Maxine #11) and the record edit form (correct a misread
 * period, Maxine #9). The finite period list loads per company scope (OQ25); a
 * failed load just leaves the dropdown with no options (and the empty option when
 * enabled).
 *
 * SEARCHABLE (Maxine 2026-08-03 ⑤): the seeded list spans every month and financial
 * year, so scrolling to "Jul 2025" was the complaint. Filtering happens client-side
 * against the already-loaded list — the list is finite and fetched once, so there is
 * nothing to debounce or re-fetch.
 * @returns {JSX.Element}
 */
export default function PeriodSelect({
  label,
  value,
  onChange,
  allowEmpty = false,
  emptyLabel = 'All periods',
  companyId,
  currentLabel,
  disabled
}: PeriodSelectProps) {
  const [periods, setPeriods] = useState<Array<PeriodLookupItem>>([]);
  const [periodsUnavailable, setPeriodsUnavailable] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    // A company switch must not leave the previous company's calendar on offer
    // while the new one loads (or if the load fails).
    setPeriods([]);
    setPeriodsUnavailable(false);
    fetchPeriods(companyId ?? undefined)
      .then((items) => {
        if (!cancelled) {
          setPeriods(items);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPeriodsUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // Fallback so the current value always matches an option, even before the list
  // loads or when the saved period is not in the (company-scoped) list — mirrors
  // EntitySelect's fallback MenuItem. Only id + label matter to the control, so
  // the synthesised entry does not need a `kind` it cannot know.
  const options: Array<Pick<PeriodLookupItem, 'id' | 'label'>> =
    value !== null && !periods.some((period) => period.id === value)
      ? [{ id: value, label: currentLabel ?? 'Current period' }, ...periods]
      : periods;
  const selected = options.find((period) => period.id === value) ?? null;

  return (
    <Autocomplete
      size="small"
      sx={{ minWidth: 180 }}
      options={options}
      value={selected}
      disabled={disabled || periodsUnavailable}
      // Only the filter bar may hold "no period"; the edit form always has one.
      disableClearable={!allowEmpty}
      onChange={(_event, next) => onChange(next === null ? null : next.id, next?.label)}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={allowEmpty ? emptyLabel : undefined}
          helperText={
            periodsUnavailable ? 'Period list unavailable — try again shortly' : undefined
          }
        />
      )}
    />
  );
}
